import {
  isSignInCodeComplete,
  normalizeSignInCode,
  parseSignInEmail,
  SIGN_IN_INVALID_EMAIL_MESSAGE,
  SIGN_IN_RESEND_SECONDS,
  type SignInResult,
  signInClient,
  signInErrorMessage,
} from "@serophin/shared";
import { type MutableRef, useEffect, useRef, useState } from "preact/hooks";

export type EmailCodeSignInStep = "email" | "code";

export type UseEmailCodeSignInOptions = {
  onSignedIn: (result: SignInResult) => void | Promise<void>;
  baseUrl?: string;
  next?: string;
};

export type EmailCodeSignIn = {
  step: EmailCodeSignInStep;
  email: string;
  code: string;
  busy: boolean;
  error: string | null;
  resendIn: number;
  setEmail: (value: string) => void;
  setCode: (value: string) => void;
  submitEmail: () => Promise<void>;
  submitCode: () => Promise<void>;
  resend: () => Promise<void>;
  changeEmail: () => void;
};

type ResendCountdown = {
  resendIn: number;
  remaining: MutableRef<number>;
  start: () => void;
  stop: () => void;
};

type EmailCodeMachine = {
  options: MutableRef<UseEmailCodeSignInOptions>;
  sentTo: MutableRef<string>;
  countdown: ResendCountdown;
  run: (task: () => Promise<void>) => Promise<void>;
  setStep: (step: EmailCodeSignInStep) => void;
  setEmail: (email: string) => void;
  storeCode: (code: string) => void;
  setError: (error: string | null) => void;
};

const useResendCountdown = (): ResendCountdown => {
  const [resendIn, setResendIn] = useState(0);
  const remaining = useRef(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const clear = () => {
    if (timer.current !== null) {
      clearInterval(timer.current);
      timer.current = null;
    }
  };
  const tick = () => {
    remaining.current = Math.max(remaining.current - 1, 0);
    setResendIn(remaining.current);
    if (remaining.current === 0) {
      clear();
    }
  };
  const start = () => {
    clear();
    remaining.current = SIGN_IN_RESEND_SECONDS;
    setResendIn(remaining.current);
    timer.current = setInterval(tick, 1000);
  };
  const stop = () => {
    clear();
    remaining.current = 0;
    setResendIn(0);
  };
  useEffect(() => clear, []);
  return { resendIn, remaining, start, stop };
};

const useBusyRunner = () => {
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const run = async (task: () => Promise<void>) => {
    if (busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    try {
      await task();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  return { busy, run };
};

const sendCode = async (machine: EmailCodeMachine, email: string) => {
  try {
    await signInClient.startEmail(email, { baseUrl: machine.options.current.baseUrl, next: machine.options.current.next });
    machine.sentTo.current = email;
    machine.setEmail(email);
    machine.storeCode("");
    machine.setError(null);
    machine.setStep("code");
    machine.countdown.start();
  } catch (error) {
    machine.setError(signInErrorMessage(error, "provider"));
  }
};

const submitEmailStep = (machine: EmailCodeMachine, email: string) =>
  machine.run(async () => {
    const parsed = parseSignInEmail(email);
    if (!parsed) {
      machine.setError(SIGN_IN_INVALID_EMAIL_MESSAGE);
      return;
    }
    await sendCode(machine, parsed);
  });

const verifyCode = (machine: EmailCodeMachine, code: string) =>
  machine.run(async () => {
    try {
      const result = await signInClient.verifyCode(machine.sentTo.current, code, { baseUrl: machine.options.current.baseUrl });
      await machine.options.current.onSignedIn(result);
    } catch (error) {
      machine.storeCode("");
      machine.setError(signInErrorMessage(error, "code"));
    }
  });

const resendCode = async (machine: EmailCodeMachine) => {
  if (machine.countdown.remaining.current > 0) {
    return;
  }
  await machine.run(() => sendCode(machine, machine.sentTo.current));
};

const updateCode = (machine: EmailCodeMachine, value: string) => {
  const code = normalizeSignInCode(value);
  machine.storeCode(code);
  machine.setError(null);
  if (isSignInCodeComplete(code)) {
    void verifyCode(machine, code);
  }
};

const backToEmail = (machine: EmailCodeMachine) => {
  machine.setStep("email");
  machine.storeCode("");
  machine.setError(null);
  machine.countdown.stop();
};

export const useEmailCodeSignIn = ({ onSignedIn, baseUrl, next }: UseEmailCodeSignInOptions): EmailCodeSignIn => {
  const options = useRef<UseEmailCodeSignInOptions>({ onSignedIn, baseUrl, next });
  options.current = { onSignedIn, baseUrl, next };
  const sentTo = useRef("");
  const [step, setStep] = useState<EmailCodeSignInStep>("email");
  const [email, setEmail] = useState("");
  const [code, storeCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const countdown = useResendCountdown();
  const { busy, run } = useBusyRunner();
  const machine: EmailCodeMachine = { options, sentTo, countdown, run, setStep, setEmail, storeCode, setError };

  return {
    step,
    email,
    code,
    busy,
    error,
    resendIn: countdown.resendIn,
    setEmail,
    setCode: (value) => updateCode(machine, value),
    submitEmail: () => submitEmailStep(machine, email),
    submitCode: () => verifyCode(machine, code),
    resend: () => resendCode(machine),
    changeEmail: () => backToEmail(machine),
  };
};
